import {
    add,
    differenceInDays,
    differenceInMonths,
    differenceInWeeks,
    differenceInYears,
    Duration,
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    format,
    isAfter,
    isBefore,
    parseISO,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
} from 'date-fns';
import { DateString } from 'zapatos/db';
import { CustomError } from '../errors';
import { Tick, TickRange, TickUnit, ValueNode, ValueSeries } from './types';

const UNITS_PER_YEAR: Record<TickUnit, number> = {
    day: 365,
    week: 52,
    month: 12,
    year: 1,
};

export const getUnitsPerYear = (unit: TickUnit): number => UNITS_PER_YEAR[unit];

const startOfUnit = (date: Date, unit: TickUnit): Date => {
    switch (unit) {
        case 'day':
            return startOfDay(date);
        case 'week':
            return startOfWeek(date);
        case 'month':
            return startOfMonth(date);
        case 'year':
            return startOfYear(date);
        default:
            throw new CustomError('Invalid unit', { unit });
    }
};

export const endOfUnit = (date: Date, unit: TickUnit): Date => {
    switch (unit) {
        case 'day':
            return endOfDay(date);
        case 'week':
            return endOfWeek(date);
        case 'month':
            return endOfMonth(date);
        case 'year':
            return endOfYear(date);
        default:
            throw new CustomError('Invalid unit', { unit });
    }
};

export const diffInUnit = (
    laterDate: Date,
    earlierDate: Date,
    unit: TickUnit
): number => {
    switch (unit) {
        case 'day':
            return differenceInDays(laterDate, earlierDate);
        case 'week':
            return differenceInWeeks(laterDate, earlierDate);
        case 'month':
            return differenceInMonths(laterDate, earlierDate);
        case 'year':
            return differenceInYears(laterDate, earlierDate);
        default:
            throw new CustomError('Invalid unit', { unit });
    }
};

export const duration = (n: number, unit: TickUnit): Duration => {
    switch (unit) {
        case 'day':
            return { days: n };
        case 'week':
            return { weeks: n };
        case 'month':
            return { months: n };
        case 'year':
            return { years: n };
        default:
            throw new CustomError('Invalid unit', { unit });
    }
};

export const toDateStr = (date: Date): DateString => {
    return format(date, 'yyyy-MM-dd') as DateString;
};

export const nowTick = (unit: TickUnit): Tick => {
    return makeTick(new Date(), unit);
};

export const makeTick = (date: string | Date, unit: TickUnit): Tick => {
    const dateObj = startOfUnit(
        typeof date === 'string' ? parseISO(date) : date,
        unit
    );
    return {
        date: toDateStr(dateObj),
        dateObj,
        unit,
    };
};

export const makeTickRange = (
    start: string,
    end: string,
    unit: TickUnit
): TickRange => {
    const startDate = parseISO(start);
    let endDate = parseISO(end);

    // Clamp to start
    if (isAfter(startDate, endDate)) endDate = startDate;

    return {
        startDate: toDateStr(startOfUnit(startDate, unit)),
        endDate: toDateStr(startOfUnit(endDate, unit)),
        unit,
    };
};

export const endOfTickDateStr = (t: Tick): string => {
    return toDateStr(endOfUnit(t.dateObj, t.unit));
};

export const arrayFromTickRange = (range: TickRange): Tick[] => {
    let cursor = parseISO(range.startDate);
    const tickArray: Tick[] = [makeTick(cursor, range.unit)];

    const endDate = parseISO(range.endDate);
    while (isBefore(cursor, endDate)) {
        const nextCursor = add(cursor, duration(1, range.unit));

        if (!isAfter(nextCursor, endDate)) {
            cursor = nextCursor;
            tickArray.push(makeTick(cursor, range.unit));
        } else {
            break;
        }
    }

    return tickArray;
};

export const findValueNode = <VN extends ValueNode>(
    vs: ValueSeries<VN>,
    tick: Tick
): VN => {
    const vsIndex = findValueNodeIndex(vs, tick);
    return vs.values[vsIndex];
};

export const findValueNodeIndex = <VN extends ValueNode>(
    vs: ValueSeries<VN>,
    tick: Tick
): number => {
    verifyUnitMatch(vs, tick);
    verifyInRange(vs, tick);

    return diffInUnit(tick.dateObj, vs.startDateObj, tick.unit);
};

const verifyUnitMatch = (a: { unit: TickUnit }, b: { unit: TickUnit }) => {
    if (a.unit !== b.unit) {
        throw new CustomError('Unit mismatch', { a, b });
    }
};

const verifyInRange = <VN extends ValueNode>(
    vs: ValueSeries<VN>,
    tick: Tick
) => {
    if (tick.date < vs.startDate || tick.date > vs.endDate) {
        throw new CustomError('Tick out of ValueSeries range', {
            tick,
            valueSeries: vs,
        });
    }
};

export const isTickInRange = (range: TickRange, tick: Tick): boolean => {
    verifyUnitMatch(range, tick);

    return !(tick.date < range.startDate || tick.date > range.endDate);
};

export const dependencyArrayFromTickRange = (range: TickRange): string[] => [
    range.startDate,
    range.endDate,
    range.unit,
];
